<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <el-button @click="$router.back()" style="margin-right: 10px;">
          <el-icon><ArrowLeft /></el-icon>
          返回
        </el-button>
        <h2>{{ aggregatorInfo.name }}</h2>
        <p class="description">{{ aggregatorInfo.description }}</p>
      </div>
      <div class="header-actions">
        <el-button type="success" @click="copyAggregatorUrl">
          <el-icon><CopyDocument /></el-icon>
          复制聚合链接
        </el-button>
        <el-button type="primary" @click="showCreateDialog = true">
          <el-icon><Plus /></el-icon>
          添加DeepLink
        </el-button>
      </div>
    </div>

    <!-- 聚合链接信息卡片 -->
    <el-card class="info-card" v-if="aggregatorInfo.id" shadow="hover">
      <template #header>
        <div class="card-header">
          <div class="card-title">
            <el-icon color="#409EFF"><InfoFilled /></el-icon>
            <span>聚合链接信息</span>
          </div>
          <el-button type="primary" size="small" @click="editAggregator">
            <el-icon><Edit /></el-icon>
            编辑
          </el-button>
        </div>
      </template>
      <div class="info-grid">
        <div class="info-item">
          <div class="info-label">
            <el-icon><Key /></el-icon>
            <span>聚合链接ID</span>
          </div>
          <div class="info-value">
            <el-tag type="primary" size="large">{{ aggregatorInfo.id }}</el-tag>
          </div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <el-icon><Link /></el-icon>
            <span>跳转链接</span>
          </div>
          <div class="info-value">
            <div class="url-container">
              <span class="url-text">{{ aggregatorUrl }}</span>
              <el-button type="text" size="small" @click="copyAggregatorUrl">
                <el-icon><CopyDocument /></el-icon>
              </el-button>
            </div>
          </div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <el-icon><CircleCheck /></el-icon>
            <span>状态</span>
          </div>
          <div class="info-value">
            <el-tag :type="aggregatorInfo.status === 'active' ? 'success' : 'danger'" size="large">
              <el-icon v-if="aggregatorInfo.status === 'active'"><CircleCheck /></el-icon>
              <el-icon v-else><CircleClose /></el-icon>
              {{ aggregatorInfo.status === 'active' ? '启用' : '禁用' }}
            </el-tag>
          </div>
        </div>
        <div class="info-item">
          <div class="info-label">
            <el-icon><Calendar /></el-icon>
            <span>创建时间</span>
          </div>
          <div class="info-value">
            <span class="time-text">{{ formatDate(aggregatorInfo.created_at) }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- DeepLink列表 -->
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <div class="card-title">
            <el-icon color="#67C23A"><Connection /></el-icon>
            <span>DeepLink管理</span>
            <el-badge :value="deeplinkList.length" type="primary" />
          </div>
          <el-button type="success" @click="showCreateDialog = true">
            <el-icon><Plus /></el-icon>
            添加DeepLink
          </el-button>
        </div>
      </template>
      
      <el-table :data="deeplinkList" v-loading="loading" stripe style="width: 100%">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="名称" min-width="180">
          <template #default="{ row }">
            <el-tooltip :content="row.name" placement="top">
              <span class="text-truncate">{{ truncateText(row.name, 20) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="url" label="链接" min-width="250">
          <template #default="{ row }">
            <el-tooltip :content="row.url" placement="top">
              <span class="text-truncate">{{ truncateText(row.url, 40) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="priority" label="优先级" width="180" align="center">
          <template #default="{ row }">
            <el-input-number 
              v-model="row.priority" 
              :min="0" 
              :max="999" 
              size="small"
              @change="updateDeeplinkField(row, 'priority')"
              style="width: 110px"
              class="priority-input"
            />
          </template>
        </el-table-column>
        <el-table-column prop="click_limit" label="点击限制" width="180" align="center">
          <template #default="{ row }">
            <el-input-number 
              v-model="row.click_limit" 
              :min="0" 
              size="small"
              @change="updateDeeplinkField(row, 'click_limit')"
              style="width: 110px"
              class="click-limit-input"
            />
            <div style="font-size: 11px; color: #909399; margin-top: 2px;">
              {{ row.click_limit === 0 ? '无限制' : '' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="current_clicks" label="当前点击" width="130" align="center">
          <template #default="{ row }">
            <span class="current-clicks-number">{{ row.current_clicks }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="140" align="center">
          <template #default="{ row }">
            <el-switch
              v-model="row.status"
              active-value="active"
              inactive-value="inactive"
              @change="handleStatusChange(row)"
              :disabled="row.status === 'exhausted'"
            />
            <div style="font-size: 11px; color: #909399; margin-top: 4px;">
              {{ getStatusText(row.status) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" align="center">
          <template #default="{ row }">
            <el-button size="small" @click="editDeeplink(row)">编辑</el-button>
            <el-button size="small" type="warning" @click="resetDeeplink(row)" v-if="row.current_clicks > 0">重置</el-button>
            <el-button size="small" type="danger" @click="deleteDeeplink(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 创建/编辑DeepLink对话框 -->
    <el-dialog 
      :title="dialogTitle" 
      v-model="showCreateDialog" 
      width="600px"
      @close="resetForm"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入DeepLink名称" />
        </el-form-item>
        <el-form-item label="链接" prop="url">
          <el-input v-model="form.url" placeholder="请输入DeepLink URL" />
        </el-form-item>
        <el-form-item label="优先级" prop="priority">
          <el-input-number v-model="form.priority" :min="0" :max="999" />
          <span class="form-tip">数字越大优先级越高</span>
        </el-form-item>
        <el-form-item label="点击限制" prop="click_limit">
          <el-input-number v-model="form.click_limit" :min="0" />
          <span class="form-tip">0表示无限制</span>
        </el-form-item>
        <el-form-item label="状态" prop="status" v-if="isEdit">
          <el-radio-group v-model="form.status" size="large">
            <el-radio label="active">
              <el-icon><CircleCheck /></el-icon>
              启用
            </el-radio>
            <el-radio label="inactive">
              <el-icon><CircleClose /></el-icon>
              禁用
            </el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <el-button size="large" @click="showCreateDialog = false">取消</el-button>
        <el-button type="primary" size="large" @click="submitForm">
          <el-icon><Check /></el-icon>
          确定
        </el-button>
      </template>
    </el-dialog>

    <!-- 编辑聚合链接对话框 -->
    <el-dialog 
      title="编辑聚合链接" 
      v-model="showEditAggregatorDialog" 
      width="600px"
      @close="resetAggregatorForm"
    >
      <el-form :model="aggregatorForm" :rules="aggregatorRules" ref="aggregatorFormRef" label-width="100px">
        <el-form-item label="名称" prop="name">
          <el-input v-model="aggregatorForm.name" placeholder="请输入聚合链接名称" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="aggregatorForm.description" type="textarea" :rows="4" placeholder="请输入描述信息" />
        </el-form-item>
        <el-form-item label="跳转方案" prop="redirect_mode">
          <el-radio-group v-model="aggregatorForm.redirect_mode" size="large">
            <el-radio label="youku_popup">
              <el-icon><VideoPlay /></el-icon>
              优酷弹窗
            </el-radio>
            <el-radio label="ios_direct">
              <el-icon><Iphone /></el-icon>
              iOS直接跳转
            </el-radio>
            <el-radio label="android_direct">
              <el-icon><Monitor /></el-icon>
              Android直接跳转
            </el-radio>
            <el-radio label="universal_redirect">
              <el-icon><VideoPlay /></el-icon>
              通用跳转
            </el-radio>
          </el-radio-group>
          <div class="form-tip">
            <el-icon><InfoFilled /></el-icon>
            <span>优酷弹窗：显示确认弹窗后跳转；直接跳转：页面加载后自动跳转；通用跳转：复制优酷弹窗方式</span>
          </div>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="aggregatorForm.status" size="large">
            <el-radio label="active">
              <el-icon><CircleCheck /></el-icon>
              启用
            </el-radio>
            <el-radio label="inactive">
              <el-icon><CircleClose /></el-icon>
              禁用
            </el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <el-button size="large" @click="showEditAggregatorDialog = false">取消</el-button>
        <el-button type="primary" size="large" @click="submitAggregatorForm">
          <el-icon><Check /></el-icon>
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft, CopyDocument, Plus, Edit, Key, Link, CircleCheck, CircleClose, Calendar, Connection, InfoFilled, Check, VideoPlay, Iphone, Monitor } from '@element-plus/icons-vue'
import { aggregatorApi, deeplinkApi } from '../api'

export default {
  name: 'AggregatorDetail',
  components: {
    ArrowLeft,
    CopyDocument,
    Plus,
    Edit,
    Key,
    Link,
    CircleCheck,
    CircleClose,
    Calendar,
    Connection,
    InfoFilled,
    Check,
    VideoPlay,
    Iphone,
    Monitor
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const route = useRoute()
    const loading = ref(false)
    const aggregatorInfo = ref({})
    const deeplinkList = ref([])
    const showCreateDialog = ref(false)
    const showEditAggregatorDialog = ref(false)
    const isEdit = ref(false)
    const formRef = ref()
    const aggregatorFormRef = ref()
    
    const form = reactive({
      id: null,
      name: '',
      url: '',
      priority: 0,
      click_limit: 0,
      status: 'active'
    })
    
    const rules = {
      name: [
        { required: true, message: '请输入名称', trigger: 'blur' }
      ],
      url: [
        { required: true, message: '请输入链接', trigger: 'blur' },
        { type: 'url', message: '请输入有效的URL', trigger: 'blur' }
      ]
    }
    
    const aggregatorForm = reactive({
      id: null,
      name: '',
      description: '',
      redirect_mode: 'youku_popup',
      status: 'active'
    })
    
    const aggregatorRules = {
      name: [
        { required: true, message: '请输入名称', trigger: 'blur' }
      ]
    }
    
    const dialogTitle = computed(() => isEdit.value ? '编辑DeepLink' : '添加DeepLink')
    
    const aggregatorUrl = computed(() => {
      const url = 'https://pages-fast.m.taobao.com.xixijizhang.cn/wow/z/hdwk/farm-ssr/bargain-backflow?account_key=test'
      return url
    })
    
    // 获取聚合链接详情
    const getAggregatorDetail = async () => {
      try {
        loading.value = true
        const response = await aggregatorApi.getDetail(props.id)
        if (response.success) {
          aggregatorInfo.value = response.data
          deeplinkList.value = response.data.deeplinks || []
        }
      } catch (error) {
        ElMessage.error('获取聚合链接详情失败')
      } finally {
        loading.value = false
      }
    }
    
    // 复制聚合链接
    const copyAggregatorUrl = async () => {
      try {
        await navigator.clipboard.writeText(aggregatorUrl.value)
        ElMessage.success('聚合链接已复制到剪贴板')
      } catch (error) {
        ElMessage.error('复制失败')
      }
    }
    
    // 编辑聚合链接
    const editAggregator = () => {
      Object.assign(aggregatorForm, {
        id: aggregatorInfo.value.id,
        name: aggregatorInfo.value.name,
        description: aggregatorInfo.value.description,
        redirect_mode: aggregatorInfo.value.redirect_mode || 'youku_popup',
        status: aggregatorInfo.value.status
      })
      showEditAggregatorDialog.value = true
    }
    
    // 提交聚合链接表单
    const submitAggregatorForm = async () => {
      try {
        await aggregatorFormRef.value.validate()
        
        const response = await aggregatorApi.update(aggregatorForm.id, aggregatorForm)
        if (response.success) {
          ElMessage.success('更新成功')
          showEditAggregatorDialog.value = false
          getAggregatorDetail()
        }
      } catch (error) {
        ElMessage.error('更新失败')
      }
    }
    
    // 重置聚合链接表单
    const resetAggregatorForm = () => {
      Object.assign(aggregatorForm, {
        id: null,
        name: '',
        description: '',
        redirect_mode: 'youku_popup',
        status: 'active'
      })
      aggregatorFormRef.value?.resetFields()
    }
    
    // 编辑DeepLink
    const editDeeplink = (row) => {
      isEdit.value = true
      Object.assign(form, row)
      showCreateDialog.value = true
    }
    
    // 重置DeepLink
    const resetDeeplink = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确定要重置DeepLink"${row.name}"的点击次数吗？`,
          '确认重置',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        const response = await deeplinkApi.reset(row.id)
        if (response.success) {
          ElMessage.success('重置成功')
          getAggregatorDetail()
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('重置失败')
        }
      }
    }
    
    // 删除DeepLink
    const deleteDeeplink = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除DeepLink"${row.name}"吗？此操作不可恢复！`,
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        const response = await deeplinkApi.delete(row.id)
        if (response.success) {
          ElMessage.success('删除成功')
          getAggregatorDetail()
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }
    
    // 提交表单
    const submitForm = async () => {
      try {
        await formRef.value.validate()
        
        const formData = {
          ...form,
          aggregator_id: props.id
        }
        
        let response
        if (isEdit.value) {
          response = await deeplinkApi.update(form.id, formData)
        } else {
          response = await deeplinkApi.create(formData)
        }
        
        if (response.success) {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          showCreateDialog.value = false
          getAggregatorDetail()
        }
      } catch (error) {
        ElMessage.error('操作失败')
      }
    }
    
    // 重置表单
    const resetForm = () => {
      isEdit.value = false
      Object.assign(form, {
        id: null,
        name: '',
        url: '',
        priority: 0,
        click_limit: 0,
        status: 'active'
      })
      formRef.value?.resetFields()
    }
    
    // 获取状态类型
    const getStatusType = (status) => {
      const types = {
        active: 'success',
        inactive: 'info',
        exhausted: 'warning'
      }
      return types[status] || 'info'
    }
    
    // 获取状态文本
    const getStatusText = (status) => {
      const texts = {
        active: '启用',
        inactive: '禁用',
        exhausted: '已耗尽'
      }
      return texts[status] || '未知'
    }
    
    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return ''
      return new Date(dateString).toLocaleString('zh-CN')
    }
    
    // 截断文本
    const truncateText = (text, maxLength) => {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    }
    
    // 更新DeepLink字段
    const updateDeeplinkField = async (row, field) => {
      try {
        const formData = {
          name: row.name,
          url: row.url,
          priority: row.priority,
          click_limit: row.click_limit,
          status: row.status,
          aggregator_id: props.id
        }
        
        const response = await deeplinkApi.update(row.id, formData)
        if (response.success) {
          ElMessage.success('更新成功')
          getAggregatorDetail()
        }
      } catch (error) {
        ElMessage.error('更新失败')
        // 重新获取数据以恢复原值
        getAggregatorDetail()
      }
    }
    
    // 处理状态切换
    const handleStatusChange = async (row) => {
      try {
        const formData = {
          name: row.name,
          url: row.url,
          priority: row.priority,
          click_limit: row.click_limit,
          status: row.status,
          aggregator_id: props.id
        }
        
        const response = await deeplinkApi.update(row.id, formData)
        if (response.success) {
          ElMessage.success('状态更新成功')
          getAggregatorDetail()
        }
      } catch (error) {
        ElMessage.error('状态更新失败')
        // 重新获取数据以恢复原值
        getAggregatorDetail()
      }
    }
    
    onMounted(() => {
      getAggregatorDetail()
    })
    
    return {
      loading,
      aggregatorInfo,
      deeplinkList,
      showCreateDialog,
      showEditAggregatorDialog,
      isEdit,
      form,
      rules,
      aggregatorForm,
      aggregatorRules,
      formRef,
      aggregatorFormRef,
      dialogTitle,
      aggregatorUrl,
      getAggregatorDetail,
      copyAggregatorUrl,
      editAggregator,
      submitAggregatorForm,
      resetAggregatorForm,
      editDeeplink,
      resetDeeplink,
      deleteDeeplink,
      submitForm,
      resetForm,
      getStatusType,
      getStatusText,
      formatDate,
      truncateText,
      updateDeeplinkField,
      handleStatusChange
    }
  }
}
</script>

<style scoped>
.page-container {
  width: 100%;
  padding: 20px;
  min-height: calc(100vh - 130px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f2f5;
}

.page-header h2 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 28px;
  font-weight: 600;
}

.description {
  margin: 0;
  color: #909399;
  font-size: 16px;
  font-weight: 400;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.info-card {
  margin-bottom: 30px;
  border-radius: 16px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2f5 100%);
  border-radius: 12px;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;
}

.info-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(0, 0, 0, 0.1);
}

.info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #606266;
  font-size: 14px;
}

.info-value {
  display: flex;
  align-items: center;
  gap: 8px;
}

.url-container {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.url-text {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid #e0e0e0;
  flex: 1;
  word-break: break-all;
}

.time-text {
  color: #606266;
  font-size: 14px;
  font-weight: 500;
}

.form-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

.text-truncate {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 数字显示样式 */
.priority-input :deep(.el-input__inner),
.click-limit-input :deep(.el-input__inner) {
  font-size: 16px;
  font-weight: 600;
}

.current-clicks-number {
  font-size: 18px;
  font-weight: 700;
  color: #409EFF;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-container {
    padding: 15px;
  }
  
  .page-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .page-header h2 {
    font-size: 24px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .info-item {
    padding: 16px;
  }
  
  .url-text {
    font-size: 12px;
    padding: 6px 10px;
  }
}
</style>
